import {
  Input,
  Form,
  Row,
  Col,
  Select,
  Button,
  Tabs,
  Table,
  TableColumnType,
  Pagination,
  Flex,
  Image,
} from 'antd';
import './index.css';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSuggestionReason, getSuggestionList } from '@/api/suggestion';
import {
  Suggestion,
  SuggestionListQuery,
  SuggestionStatus,
} from '@/types/suggestion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { FORMAT_TIME } from '@/types/common';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';

export const SUGGESTION_IDS_KEY = 'suggestion_ids';
export const SUGGESTION_INDEX_KEY = 'suggestion_index';

const SuggestionPage = () => {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const pageSize = isMobile ? 10 : 20;
  const [form] =
    Form.useForm<Omit<SuggestionListQuery, 'currentPage' | 'pageSize'>>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('all');

  useEffect(() => {
    form.setFieldsValue(Object.fromEntries(searchParams));
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
    const tab = searchParams.get('status') || 'all';
    setTab(tab);
  }, [searchParams, form]);

  const { data: suggestionReasonList } = useQuery({
    queryKey: ['suggestionReasonList'],
    queryFn: getSuggestionReason,
    refetchOnWindowFocus: false,
  });

  const suggestionReasonOptions = useMemo(() => {
    return suggestionReasonList?.data.map((item) => ({
      label: item.feedbackName,
      value: item.feedbackCode,
    }));
  }, [suggestionReasonList]);

  const {
    data: suggestionList,
    isLoading,
    refetch: refetchSuggestionList,
  } = useQuery({
    queryKey: ['suggestionList', page, tab],
    queryFn: async () => {
      const formValues = form.getFieldsValue();
      // 处理空字符串
      const values = Object.fromEntries(
        Object.entries(formValues).map(([key, value]) => [
          key,
          value === '' ? undefined : value,
        ])
      );

      const params: SuggestionListQuery = {
        ...values,
        currentPage: page,
        pageSize,
        status: tab !== 'all' ? parseInt(tab) : undefined,
      };
      const resp = await getSuggestionList(params);
      return resp.data;
    },
  });

  const handleDetailClick = (index: number) => {
    const ids = suggestionList?.records.map((record) => record.id) || [];
    const params = new URLSearchParams(searchParams);
    params.set(SUGGESTION_IDS_KEY, ids.join(','));
    params.set(SUGGESTION_INDEX_KEY, index.toString());
    navigate(`/suggestion/check?${params.toString()}`);
  };

  // 设备类型映射
  const deviceTypeMap: Record<number, string> = {
    1: 'IOS APP',
    2: 'Android APP',
    3: 'iOS H5',
    4: 'Android H5',
    5: 'Other/PC',
  };

  const columns: TableColumnType<Suggestion>[] = [
    {
      title: 'Suggestion ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'SUPPID',
      dataIndex: 'suppId',
      key: 'suppId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => record.name || record.nickName || '-',
    },
    {
      title: '会员类型',
      dataIndex: 'vipName',
      key: 'vipName',
      render: (value: string) => {
        if (!value) return '-';
        return value.replace(/Plus/g, 'Pro');
      },
    },
    {
      title: '设备类型',
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (_, record) => {
        return record.deviceType
          ? deviceTypeMap[record.deviceType] || '-'
          : '-';
      },
    },
    {
      title: 'Pictures',
      dataIndex: 'userImageUrls',
      key: 'userImageUrls',
      render: (_, record) => {
        const images = record.userImageUrls
          ? record.userImageUrls.split(',')
          : [];
        if (images.length === 0) {
          return '-';
        }
        return <Image src={images[0]} width={100} preview={true} />;
      },
    },
    {
      title: 'Suggested Reason',
      dataIndex: 'feedbackName',
      key: 'feedbackName',
    },
    {
      title: 'Description',
      dataIndex: 'userRemark',
      key: 'userRemark',
      render: (_, record) => {
        return <div style={{ maxWidth: 500 }}>{record.userRemark || '-'}</div>;
      },
    },
    {
      title: 'Last Operation',
      dataIndex: 'operateUserName',
      key: 'operateUserName',
      render: (_, record) => (
        <>
          <div>{record.updateUserName || '(Unknown Name)'}</div>
          <div>{dayjs(record.updateTime).format(FORMAT_TIME)}</div>
        </>
      ),
    },
    {
      title: 'Feedback Time (JST)',
      dataIndex: 'createTime',
      key: 'feedbackTime',
      render: (_, record) => {
        if (!record.createTime) return '-';
        return dayjs.utc(record.createTime).add(9, 'hour').format(FORMAT_TIME);
      },
    },
    {
      title: 'Operation',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record, index) => (
        <Button type="link" onClick={() => handleDetailClick(index)}>
          Check Details
        </Button>
      ),
    },
  ];

  const handleTabChange = (key: string) => {
    setPage(1);
    setTab(key);
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (key !== 'all') {
      params.set('status', key);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    Object.entries(form.getFieldsValue()).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });
    params.set('page', '1');
    setSearchParams(params);
    setPage(1);
    void refetchSuggestionList();
  };

  return (
    <div className="suggestion-page-container">
      <Form
        form={form}
        labelCol={{
          xs: 24,
          sm: 12,
          lg: 6,
        }}
        wrapperCol={{
          xs: 24,
          sm: 16,
          lg: 18,
        }}
        labelAlign="left"
        onFinish={handleSearch}
      >
        <Row gutter={24}>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item name="id" label="Suggestion ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item name="suppId" label="suppId">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item name="feedbackCode" label="Suggested Reason">
              <Select
                options={suggestionReasonOptions}
                allowClear
                size="small"
              />
            </Form.Item>
          </Col>
          {/* <Col xs={24} sm={12} lg={8}>
            <Form.Item name="suggestedData" label="Suggested Data">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col> */}
        </Row>
        <div>
          <Button type="primary" htmlType="submit">
            Search
          </Button>
        </div>
      </Form>
      <div style={{ paddingTop: '10px' }}>
        <Tabs
          type="card"
          activeKey={tab}
          items={[
            { key: 'all', label: 'All' },
            { key: SuggestionStatus.PENDING.toString(), label: 'Pending' },
            { key: SuggestionStatus.ARCHIVED.toString(), label: 'Archived' },
          ]}
          className="tabs-container"
          onChange={handleTabChange}
          size="small"
        />
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={suggestionList?.records}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={false}
        size="small"
      />
      <Flex style={{ marginTop: 20, flexDirection: 'row-reverse' }}>
        <Pagination
          total={suggestionList?.total}
          pageSize={pageSize}
          current={page}
          showSizeChanger={false}
          onChange={(page) => {
            setPage(page);
            const params = new URLSearchParams(searchParams);
            params.set('page', page.toString());
            setSearchParams(params);
          }}
          size={isMobile ? 'small' : 'default'}
        />
      </Flex>
    </div>
  );
};

export default SuggestionPage;
